export type ImagePayload = { dataUrl: string; format: "png" | "jpeg" };

import type { SurfImageCrop } from "@vida/surf-core";
import { prepareRasterDisplayUrl } from "../utils/rasterDisplayPrep.js";

export async function loadImagePayload(
  url: string,
  opts?: { chromaKey?: boolean; chromaHex?: string },
): Promise<ImagePayload> {
  let fetchUrl = url;
  if (opts?.chromaKey && opts.chromaHex && typeof document !== "undefined") {
    try {
      const prepared = await prepareRasterDisplayUrl(url, opts.chromaHex);
      fetchUrl = prepared;
    } catch {
      fetchUrl = url;
    }
  }

  // `cache: no-store`: the same S3 URL is first loaded by <img> (display) WITHOUT an
  // Origin header → S3 caches a 200 with no `Access-Control-Allow-Origin`. A later
  // CORS fetch() would reuse that cached response and fail. Force a fresh request.
  const res = await fetch(fetchUrl, { cache: "no-store" });
  if (!res.ok) throw new Error(`Immagine export (${res.status})`);
  const blob = await res.blob();

  if (fetchUrl.startsWith("blob:") && fetchUrl !== url) {
    URL.revokeObjectURL(fetchUrl);
  }

  // jsPDF only embeds PNG/JPEG. Disk uploads may be WebP/GIF/HEIC (and a mislabeled
  // type would make addImage throw). Pass PNG/JPEG straight through; re-encode any
  // other decodable format to PNG via canvas so export never drops the image.
  const mime = blob.type.toLowerCase();
  if (mime.includes("png")) return { dataUrl: await blobToDataUrl(blob), format: "png" };
  if (mime.includes("jpeg") || mime.includes("jpg")) {
    return { dataUrl: await blobToDataUrl(blob), format: "jpeg" };
  }
  return { dataUrl: await reencodeToPngDataUrl(blob), format: "png" };
}

/** Decode a data URL into an <img> element. */
async function decodeDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("decode immagine fallita"));
    el.src = dataUrl;
  });
}

/**
 * Natural aspect (width / height) from decoded pixels — same source as canvas <img>.
 * Returns null when decode fails or dimensions are missing.
 */
export async function imageAspectFromPayload(payload: ImagePayload): Promise<number | null> {
  if (typeof document === "undefined") return null;
  try {
    const img = await decodeDataUrl(payload.dataUrl);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!(w > 0 && h > 0)) return null;
    return w / h;
  } catch {
    return null;
  }
}

/**
 * Bake a crop rectangle (source fractions 0..1) into the pixels of a payload.
 * The result is a PNG containing ONLY the cropped region — so PDF/PPTX draw it
 * full-rect on the widget box, matching the canvas crop mapping.
 */
export async function cropImagePayload(
  payload: ImagePayload,
  crop: SurfImageCrop,
): Promise<ImagePayload> {
  if (typeof document === "undefined") return payload;
  const img = await decodeDataUrl(payload.dataUrl);
  const natW = img.naturalWidth || img.width;
  const natH = img.naturalHeight || img.height;
  if (!natW || !natH) return payload;
  const sx = Math.max(0, Math.round(crop.x * natW));
  const sy = Math.max(0, Math.round(crop.y * natH));
  const sw = Math.max(1, Math.min(natW - sx, Math.round(crop.w * natW)));
  const sh = Math.max(1, Math.min(natH - sy, Math.round(crop.h * natH)));
  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d");
  if (!ctx) return payload;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return { dataUrl: canvas.toDataURL("image/png"), format: "png" };
}

/**
 * Bake a center-crop "cover" region onto the box aspect (= object-fit: cover).
 * The result is a PNG whose aspect matches the widget box, so PDF/PPTX draw it
 * full-rect without any clip() — identical framing to the canvas `cover` render.
 */
export async function coverImagePayload(
  payload: ImagePayload,
  boxAspect: number,
): Promise<ImagePayload> {
  if (typeof document === "undefined" || !(boxAspect > 0)) return payload;
  const img = await decodeDataUrl(payload.dataUrl);
  const natW = img.naturalWidth || img.width;
  const natH = img.naturalHeight || img.height;
  if (!natW || !natH) return payload;
  const imgAspect = natW / natH;
  let sx = 0;
  let sy = 0;
  let sw = natW;
  let sh = natH;
  if (imgAspect > boxAspect) {
    sw = Math.round(natH * boxAspect);
    sx = Math.round((natW - sw) / 2);
  } else {
    sh = Math.round(natW / boxAspect);
    sy = Math.round((natH - sh) / 2);
  }
  sw = Math.max(1, Math.min(natW - sx, sw));
  sh = Math.max(1, Math.min(natH - sy, sh));
  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d");
  if (!ctx) return payload;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return { dataUrl: canvas.toDataURL("image/png"), format: "png" };
}

/**
 * Replace layer payloads with center-cropped "cover" variants for every layer
 * whose export fit is cover. Mutates and returns `layerPayloads`.
 */
export async function applyLayerCoversToPayloads(
  covers: Array<{ layerId: string; boxAspect: number }>,
  layerPayloads: Map<string, ImagePayload>,
): Promise<Map<string, ImagePayload>> {
  await Promise.all(
    covers.map(async ({ layerId, boxAspect }) => {
      const base = layerPayloads.get(layerId);
      if (!base) return;
      try {
        layerPayloads.set(layerId, await coverImagePayload(base, boxAspect));
      } catch {
        /* keep the original payload on failure */
      }
    }),
  );
  return layerPayloads;
}

/**
 * Replace layer payloads with cropped variants for every layer carrying a crop.
 * Mutates and returns `layerPayloads`.
 */
export async function applyLayerCropsToPayloads(
  crops: Array<{ layerId: string; crop: SurfImageCrop }>,
  layerPayloads: Map<string, ImagePayload>,
): Promise<Map<string, ImagePayload>> {
  await Promise.all(
    crops.map(async ({ layerId, crop }) => {
      const base = layerPayloads.get(layerId);
      if (!base) return;
      try {
        layerPayloads.set(layerId, await cropImagePayload(base, crop));
      } catch {
        /* keep the uncropped payload on failure */
      }
    }),
  );
  return layerPayloads;
}

/** Decode an arbitrary image blob and re-encode as a PNG data URL (jsPDF-safe). */
async function reencodeToPngDataUrl(blob: Blob): Promise<string> {
  if (typeof document === "undefined") {
    // No DOM (SSR/worker): best effort — return raw data URL.
    return blobToDataUrl(blob);
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("decode immagine fallita"));
      el.src = objectUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d non disponibile");
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

export type PrefetchImagesOpts = {
  /** layerId → chroma hex for client keying */
  chromaKeyByLayerId?: Record<string, string>;
  layerUrls?: Record<string, string>;
};

export async function prefetchImages(
  urls: string[],
  opts?: PrefetchImagesOpts,
): Promise<Map<string, ImagePayload>> {
  const cache = new Map<string, ImagePayload>();
  const unique = [...new Set(urls.filter(Boolean))];
  const chromaByUrl = new Map<string, string>();
  if (opts?.chromaKeyByLayerId && opts.layerUrls) {
    for (const [layerId, url] of Object.entries(opts.layerUrls)) {
      const hex = opts.chromaKeyByLayerId[layerId];
      if (hex) chromaByUrl.set(url, hex);
    }
  }

  await Promise.all(
    unique.map(async (url) => {
      try {
        const chromaHex = chromaByUrl.get(url);
        cache.set(
          url,
          await loadImagePayload(url, {
            chromaKey: Boolean(chromaHex),
            chromaHex,
          }),
        );
      } catch {
        /* skip missing image; export falls back to placeholder/solid */
      }
    }),
  );
  return cache;
}

/** Map layerId → payload using a URL cache keyed by presigned URL (F14). */
export function layerPayloadsFromUrls(
  layerUrls: Record<string, string>,
  byUrl: Map<string, ImagePayload>,
): Map<string, ImagePayload> {
  const out = new Map<string, ImagePayload>();
  for (const [layerId, url] of Object.entries(layerUrls)) {
    const payload = byUrl.get(url);
    if (payload) out.set(layerId, payload);
  }
  return out;
}

export function collectExportImageUrls(
  slideBackgroundUrls: Record<string, string>,
  layerImageUrls: Record<string, string>,
): string[] {
  const urls = new Set<string>(Object.values(layerImageUrls));
  for (const url of Object.values(slideBackgroundUrls)) {
    if (url) urls.add(url);
  }
  return [...urls];
}
